import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Button } from '../../ui/button'
import useFileUploader from 'hooks/useFileUploader'
import { ArrowLeft, ArrowRight, AlertCircle, Trash2, Plus } from 'lucide-react'
import RichTextEditor from 'components/RichTextEditor'
import type { DealFormData } from '../../../lib/types'
import { validateProjectSummary } from '../../../lib/step-validators'
import { OverviewDocumentsSection } from './overview'

interface StepProjectSummaryProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  headingId?: string
  onValidationError?: (errorMessage: string) => void
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  requestChanges?: Record<string, string>
}

export function StepProjectSummary({
  formData,
  onComplete,
  onUpdate,
  onBack,
  headingId,
  onValidationError,
  onValidationErrors,
  requestChanges,
}: StepProjectSummaryProps) {
  const { uploadFile } = useFileUploader()
  const [deckUploading, setDeckUploading] = useState(false)
  const [deckUploadError, setDeckUploadError] = useState<string | undefined>()
  const handlePitchDeckSelected = (file: File | null) => {
    updateField('pitchDeck', file)
    if (!file) {
      onUpdate && onUpdate({ pitchDeck: null, pitchDeckUrl: '' } as any)
      setDeckUploading(false)
      setDeckUploadError(undefined)
      setLocalData((prev) => ({ ...prev, pitchDeck: null, pitchDeckUrl: '' }))
      return
    }
    setDeckUploading(true)
    setDeckUploadError(undefined)
    uploadFile(
      { file, path: `submission/pitchdeck-${Date.now()}-${file.name}` },
      (res: { url: string }) => {
        onUpdate && onUpdate({ pitchDeckUrl: res.url } as any)
        setDeckUploading(false)
        setDeckUploadError(undefined)
        setLocalData((prev) => ({ ...prev, pitchDeckUrl: res.url }))
      },
      () => {
        setDeckUploading(false)
        setDeckUploadError('Upload failed. Please try again.')
      },
    )
  }
  const [localData, setLocalData] = useState<Partial<DealFormData>>({
    description: formData.description || '',
    salesDetails: formData.salesDetails || '',
    pitchDeck: formData.pitchDeck || null,
    pitchDeckUrl: (formData as any).pitchDeckUrl || '',
    researchReport: formData.researchReport || '',
    researchNotes: formData.researchNotes || '',
    requestResearch: formData.requestResearch || false,
    researchPath:
      (formData as any).requestResearchSupport === true
        ? 'outsourced'
        : formData.researchReport
        ? 'link'
        : '',
    requestResearchSupport: (formData as any).requestResearchSupport || false,
    investors: formData.investors || [''],
    salesType: (formData as any).salesType || '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    setLocalData({
      description: formData.description || '',
      salesDetails: formData.salesDetails || '',
      pitchDeck: formData.pitchDeck || null,
      pitchDeckUrl: (formData as any).pitchDeckUrl || '',
      researchReport: formData.researchReport || '',
      researchNotes: formData.researchNotes || '',
      requestResearch: formData.requestResearch || false,
      researchPath:
        (formData as any).requestResearchSupport === true
          ? 'outsourced'
          : formData.researchReport
          ? 'link'
          : '',
      requestResearchSupport: (formData as any).requestResearchSupport || false,
      investors: formData.investors || [''],
      salesType: (formData as any).salesType || '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateField = (field: string, value: any) => {
    if (field === 'researchPath') {
      setLocalData((prev) => ({
        ...prev,
        [field]: value,
        requestResearchSupport: value === 'outsourced',
        researchReport: value === 'outsourced' ? '' : prev.researchReport,
      }))
      if (errors.researchReport)
        setErrors((prev) => ({ ...prev, researchReport: '' }))
      onUpdate &&
        onUpdate({
          researchPath: value,
          requestResearchSupport: value === 'outsourced',
          researchReport:
            value === 'outsourced' ? '' : localData.researchReport,
        } as Partial<DealFormData>)
      return
    }
    setLocalData((prev) => ({ ...prev, [field]: value }))
    setErrors((prev) => ({ ...prev, [field]: '' }))
    onUpdate && onUpdate({ [field]: value } as Partial<DealFormData>)
  }

  const toggleResearchPath = (value: 'link' | 'outsourced') => {
    const newValue = localData.researchPath === value ? '' : value
    updateField('researchPath', newValue)
  }

  const addInvestor = () => {
    const newInvestors = [...(localData.investors || []), '']
    setLocalData((prev) => ({
      ...prev,
      investors: newInvestors,
    }))
    setErrors((prev) => ({ ...prev, capTable: '' }))
    onUpdate && onUpdate({ investors: newInvestors } as Partial<DealFormData>)
  }
  const removeInvestor = (index: number) => {
    const newInvestors = (localData.investors || []).filter(
      (_, i) => i !== index,
    )
    setLocalData((prev) => ({
      ...prev,
      investors: newInvestors,
    }))
    setErrors((prev) => ({ ...prev, capTable: '' }))
    onUpdate && onUpdate({ investors: newInvestors } as Partial<DealFormData>)
  }
  const updateInvestor = (index: number, value: string) => {
    const newInvestors = (localData.investors || []).map((inv, i) =>
      i === index ? value : inv,
    )
    setLocalData((prev) => ({
      ...prev,
      investors: newInvestors,
    }))
    setErrors((prev) => ({ ...prev, capTable: '' }))
    onUpdate && onUpdate({ investors: newInvestors } as Partial<DealFormData>)
  }

  const isPublicOrPrivateSale = ['Public Sale', 'Private Sale'].includes(
    (localData as any).salesType,
  )

  const renderCapTableCard = () => (
    <Card id="capTable">
      <CardHeader>
        <CardTitle>
          Cap Table
          {isPublicOrPrivateSale ? '' : ' (optional)'}
        </CardTitle>
        <CardDescription className="text-sm font-normal text-neutral-600 mt-1">
          Add each investor's name manually.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {(localData.investors || []).map((investor, index) => (
            <div key={index} className="space-y-1">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Investor Name"
                  value={investor}
                  onChange={(e) => updateInvestor(index, e.target.value)}
                  className="flex-1 h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:border-neutral-800 focus:ring-0"
                />
                {(localData.investors || []).length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeInvestor(index)}
                    className="h-10 w-10 flex items-center justify-center hover:bg-neutral-100 rounded-lg"
                    aria-label="Remove investor"
                  >
                    <Trash2 className="w-4 h-4 text-neutral-500" />
                  </button>
                )}
              </div>
              {requestChanges?.[`capTable-${index}`] && (
                <p className="text-status-error text-xs inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {requestChanges[`capTable-${index}`]}
                </p>
              )}
            </div>
          ))}
          {errors.capTable && (
            <p className="text-status-error text-xs flex items-center gap-1 mt-2">
              <AlertCircle className="w-3 h-3" />
              {errors.capTable}
            </p>
          )}
          {requestChanges?.capTable && (
            <p className="text-status-error text-xs flex items-center gap-1 mt-2">
              <AlertCircle className="w-3 h-3" />
              {requestChanges.capTable}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addInvestor}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Investor
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  const handleSubmit = () => {
    const { errors: validationErrors, isValid } = validateProjectSummary(
      localData,
      isPublicOrPrivateSale,
      (localData as any).salesType,
    )
    setErrors(validationErrors)
    if (!isValid) {
      const sectionLabels: Record<string, string> = {
        description: 'Project Narrative',
        pitchDeck: 'Pitch Deck',
        researchReport: 'Research Materials',
        capTable: 'Cap Table',
      }

      const mappedErrors = Object.keys(validationErrors).map((id) => ({
        id,
        label: sectionLabels[id] || id,
      }))

      if (onValidationErrors) {
        onValidationErrors(mappedErrors)
      } else {
        onValidationError?.(
          'Please complete all required fields before continuing.',
        )
      }
      return
    }
    onValidationErrors?.([])
    onValidationError?.('')
    onComplete(localData as Partial<DealFormData>)
  }

  return (
    <div className="space-y-6">
      <Card id="description">
        <CardHeader>
          <CardTitle>Project Narrative</CardTitle>
          <CardDescription>
            Recomended structure: Project Overview, Traction, Token Utility &
            Economics, Roadmap, Competitive Landscape, Team, Investment Thesis,
            Risk Evaluation, Project Coverage.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`rounded-lg ${
              errors.description
                ? 'border border-status-error bg-red-50/40'
                : 'border border-transparent'
            }`}
          >
            <RichTextEditor
              value={(localData as Record<string, string>).description || ''}
              onChange={(value: string) => updateField('description', value)}
              placeholder=""
              name="project-overview"
              minHeight={320}
            />
          </div>
          <div className="flex flex-col gap-1">
            {errors.description && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.description}
              </p>
            )}
            {requestChanges?.description && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.description}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card id="salesDetails">
        <CardHeader>
          <CardTitle>Sale Details</CardTitle>
          <CardDescription>
            Describe the sale structure, terms, and any relevant context for
            investors.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`rounded-lg ${
              errors.salesDetails
                ? 'border border-status-error bg-red-50/40'
                : 'border border-transparent'
            }`}
          >
            <RichTextEditor
              value={(localData as Record<string, string>).salesDetails || ''}
              onChange={(value: string) => updateField('salesDetails', value)}
              placeholder="Describe the sale."
              name="sales-details"
              minHeight={320}
            />
          </div>
          <div className="flex flex-col gap-1">
            {errors.salesDetails && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.salesDetails}
              </p>
            )}
            {requestChanges?.salesDetails && (
              <p className="text-status-error text-xs inline-flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {requestChanges.salesDetails}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {localData.salesType !== 'OTC Deal' && (
        <OverviewDocumentsSection
          localData={{
            pitchDeck: localData.pitchDeck || null,
            researchPath: localData.researchPath || '',
            researchReport: localData.researchReport || '',
          }}
          errors={errors}
          setErrors={setErrors}
          deckUploading={deckUploading}
          deckUploadError={deckUploadError}
          onPitchDeckSelected={handlePitchDeckSelected}
          updateField={updateField}
          toggleResearchPath={toggleResearchPath}
          requireCoreDocs={isPublicOrPrivateSale}
          requestChanges={requestChanges}
        />
      )}

      {localData.salesType !== 'OTC Deal' && renderCapTableCard()}

      <div className="flex justify-between pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
